import { useMutation, useQueryClient } from "@tanstack/react-query"
import { RefreshCw } from "lucide-react"

import { Button } from "@/components/button"
import { StatusAlert } from "@/components/shared/status-alert"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Spinner } from "@/components/ui/spinner"
import {
  absenceTrackingApi,
  type AbsenceRequest,
} from "@/lib/absence-tracking-api"

import { AbsenceTable } from "./absence-table"

const absenceDashboardQueryKey = ["absence-tracking", "me"] as const

export function AbsenceActiveCard({
  activeItems,
  isLoading,
  isError,
  onRetry,
}: {
  activeItems: AbsenceRequest[]
  isLoading: boolean
  isError: boolean
  onRetry?: () => void
}) {
  const queryClient = useQueryClient()

  const cancelMutation = useMutation({
    mutationFn: absenceTrackingApi.cancel,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: absenceDashboardQueryKey,
      })
    },
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle>Upcoming and active</CardTitle>
        <CardDescription>Your current and upcoming absences.</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div
            className="flex items-center gap-2 py-4 text-sm text-muted-foreground"
            role="status"
          >
            <Spinner />
            <span>Loading current absences...</span>
          </div>
        ) : null}
        {isError ? (
          <StatusAlert tone="danger" className="mb-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <span>Failed to load your active absences.</span>
              {onRetry ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={onRetry}
                >
                  <RefreshCw className="size-3.5" />
                  Retry
                </Button>
              ) : null}
            </div>
          </StatusAlert>
        ) : null}
        <AbsenceTable
          items={activeItems}
          emptyLabel="No active or upcoming absences."
          actionLabel={cancelMutation.isPending ? "Cancelling..." : "Cancel"}
          disabledRequestId={cancelMutation.variables}
          onAction={(requestId) => void cancelMutation.mutateAsync(requestId)}
        />
      </CardContent>
    </Card>
  )
}
